import React, { useState, useEffect, forwardRef } from "react";
import { useForm } from "react-hook-form";
import Loader from "react-loader-spinner";
import { StaticImage } from "gatsby-plugin-image";
import Confetti from "react-confetti";
import { Link } from "gatsby";

// utils
import { formatNumber } from "../../utils";

// constants
import constant from "../../constants";

// api calls
import { doValidatePincode } from "../../actions/bookPackActions";

// components
import BookPackCouponModal from "./BookPackCouponModal";
import AlertPopUp from "../AlertPopUp/AlertPopUp";
import DropDown from "../DropDown/DropDown";
import PlanCards from "./PlanCards";
import ToggleButton from "./ToggleButton/ToggleButton";
import ProgramIncludes from "../WbdtPlans/ProgramIncludes/ProgramIncludes";

// css
import * as styles from "./BookPack.module.css";

// Confetti
var windowWidth, windowHeight;

if (typeof window !== `undefined`) {
  windowWidth = parseInt(window.innerWidth);
  windowHeight = parseInt(window.innerHeight);

  if (windowWidth <= 768) {
    windowWidth = parseInt(window.innerWidth);
    windowHeight = "2500px";
  } else {
    windowWidth = parseInt(window.innerWidth);
    windowHeight = "2000px";
  }
}

const BookPackPilotForm = forwardRef((props, ref) => {
  const [hasApplied, setHasApplied] = useState(false); // Used to show coupon section
  const [successMsg, setsuccessMsg] = useState("");
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCouponCanvasModal, setShowCouponCanvasModal] = useState(false);
  const [showAlertPopUp, setShowAlertPopUp] = useState(false); // Global Alert Popup Message for non-serviceable pincode
  const [selectedLanguage, setSelectedLanguage] = useState([]); // selected language values ie using multiple checkboxes
  const [showLanguageError, setShowLanguageError] = useState("none"); // show error for language selection if left empty
  const [loading, setLoading] = useState(false);
  // const [validPinCode, setValidPinCode] = useState(false);

  const [city, setCity] = useState("");
  const [prevPinCode, setPrevPinCode] = useState("");

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    setValue,
  } = useForm({ mode: "all" });

  useEffect(() => {
    setValue("name", props.name || "");
    setValue("email", props.email || "");
    setValue("phone", props.phone || "");
    if (props.postalCode) {
      setValue("pinCode", props.postalCode, { shouldValidate: true });
    }
  }, [props.name, props.email, props.phone, props.postalCode, setValue]);

  const onSubmit = async data => {
    if (loading) {
      return false;
    }
    data.city = city;
    // data.pinCode = prevPinCode;
    data.language = selectedLanguage;

    if (selectedLanguage.length > 0) props.handlePayNow(data);
  };

  // Validate pin code --> Serviceable or not
  const validatePinCode = async value => {
    const watchPinCode = watch("pinCode");
    if (watchPinCode === prevPinCode) {
      return;
    }

    try {
      setLoading(true);
      // setValidPinCode(false);
      const response = await doValidatePincode(value);
      const foundPincode = response.results;
      setLoading(false);

      const city = foundPincode[0].city || "Other";

      setCity(city);
      setPrevPinCode(value);
      // setValidPinCode(true);
    } catch (error) {
      setCity("Other");
      console.log("Something went wrong");
      // return "Pin code server error";
    }
  };

  // Coupon Modal
  const handleCouponModal = () => {
    setShowCouponModal(true);
    setShowCouponCanvasModal(true);
  };

  // Handle language selection
  const handleLanguageSelection = value => {
    if (value.length > 0) {
      setShowLanguageError("none");
      setSelectedLanguage(value);
    } else {
      setShowLanguageError("block");
    }
  };

  // Calculate button price based on selected plan
  const getButtonPrice = () => {
    const priceMap = {
      installmentPlan: props.firstInstallmentPrice,
      quarterlyPlan: Math.round(props.quarterlyPlanPrice),
      monthlyPlan: Math.round(props.monthlyPlanPrice),
      annualPlan: props.annualPlanPrice,
    };
    return priceMap[props.selectedPlan] || props.annualPlanPrice;
  };

  const buttonPrice = getButtonPrice();

  // Common props for PlanCards component
  const planCardsProps = {
    annualPlanPrice: props.annualPlanPrice,
    installmentPlanPrice: props.installmentPlanPrice,
    firstInstallmentPrice: props.firstInstallmentPrice,
    handlePlan: props.handlePlan,
    selectedPlan: props.selectedPlan,
    quarterlyPlanPrice: props.quarterlyPlanPrice,
    monthlyPlanPrice: props.monthlyPlanPrice,
    showAnnualCard: props.showAnnualCard,
    showInstallmentCard: props.showInstallmentCard,
    showQuarterly: props.showQuarterly,
    showMonthly: props.showMonthly,
    annualOfferPrice: props.annualOfferPrice,
    installmentOfferPrice: props.installmentOfferPrice,
    quarterlyOfferPrice: props.quarterlyOfferPrice,
    monthlyOfferPrice: props.monthlyOfferPrice,
  };

  // Render PlanCards based on toggle button value
  const renderPlanCards = () => {
    const toggleValues = [
      "pilot_diabetes",
      "pilot_obesity",
      "pilot_pre_diabetes",
      "pilot_metabolic_wellness",
      "pilot_pcos",
      "pilot_hypertension",
      "pilot_fatty_liver",
    ];

    if (toggleValues.includes(props.toggleButtonValue)) {
      return <PlanCards {...planCardsProps} />;
    }
    return null;
  };

  return (
    <>
      <div>
        {showConfetti && <Confetti width={windowWidth} height={windowHeight} />}
        <div data-testid="bookPackForm">
          <div className={styles.bookPackFormV2__topContntSec}>
            {/* Text */}
            <div className={styles.bookPackFormV2__topContntSec__hdng}>
              <h3>What do you want to take control of?</h3>
            </div>

            <ToggleButton handleToggleButton={props.handleToggleButton} />

            <ProgramIncludes />

            <div className="bgWhiteColor">{renderPlanCards()}</div>

            {/* Form */}
            <div id="wbdt-form" className={`${styles.formSection}`} ref={ref}>
              <div
                className={styles.bookpackForm__formHdng}
                data-testid="bookpack_formHdng"
              >
                <p>Complete Your Twin Health India Enrollment</p>
              </div>
              {/* Form */}
              <div className="container">
                <form
                  name="bookPack"
                  onSubmit={handleSubmit(onSubmit)}
                  data-testid="bookPackForm_form"
                  className={styles.bookPackFormV2__formSec}
                >
                  <div className="row">
                    <div className={`col-md-6`}>
                      <div
                        className={`form-group ${styles.bookpackForm_formGrp}`}
                      >
                        <label className="mb-2" htmlFor="name">
                          Full Name*
                        </label>
                        <input
                          className={
                            errors.name
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Full name"
                          id="name"
                          type="text"
                          data-testid="bookpack_name"
                          {...register("name", {
                            required: "Please enter your name",
                            pattern: {
                              value: /[a-zA-Z][a-zA-Z ]*/, // eslint-disable-line no-useless-escape
                              message: "Please enter your valid name",
                            },
                            maxLength: {
                              value: 50,
                              message: "Name is too long",
                            },
                          })}
                        />
                        {errors.name && (
                          <span className="invalid-feedback">
                            {errors.name.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`col-md-6 ${styles.bookpackForm_form_col}`}>
                      <div
                        className={`form-group ${styles.bookpackForm_formGrp}`}
                      >
                        <label className="mb-2" htmlFor="phone">
                          Mobile Number*
                        </label>
                        <input
                          className={`form-control letterSp1 ${
                            errors.phone ? "is-invalid" : ""
                          }`}
                          placeholder="Mobile number"
                          id="phone"
                          type="text"
                          inputMode="numeric"
                          maxLength="10"
                          data-testid="bookpack_phone"
                          {...register("phone", {
                            required: "Please enter your mobile number",
                            pattern: {
                              value:
                                /^\+?([1-9]{1})\)?[-. ]?([0-9]{5})[-. ]?([0-9]{4})$/, // eslint-disable-line no-useless-escape
                              message: "Please enter your valid phone number",
                            },
                            maxLength: {
                              value: 10,
                              message: "Invalid phone number",
                            },
                          })}
                        />
                        {errors.phone && (
                          <span className="invalid-feedback">
                            {errors.phone.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`col-md-6 ${styles.bookpackForm_form_col2}`}
                    >
                      <div
                        className={`form-group ${styles.bookpackForm_formGrp}`}
                      >
                        <label className="mb-2" htmlFor="email">
                          Email Id*
                        </label>
                        <input
                          className={
                            errors.email
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Email id"
                          id="email"
                          type="email"
                          data-testid="bookpack_email"
                          {...register("email", {
                            required: "Please enter your email",
                            pattern: {
                              value:
                                /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i, // eslint-disable-line no-useless-escape
                              message: "Please enter your valid email",
                            },
                          })}
                        />
                        {errors.email && (
                          <span className="invalid-feedback">
                            {errors.email.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`col-md-6 ${styles.bookpackForm_form_col2}`}
                    >
                      <div
                        className={`form-group ${styles.bookpackForm_formGrp}`}
                      >
                        <div>
                          <label className="mb-2" htmlFor="pinCode">
                            Pin Code*
                          </label>
                          <input
                            className={
                              errors.pinCode
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            id="pinCode"
                            type="text"
                            maxLength="6"
                            placeholder="Pin code"
                            inputMode="numeric"
                            {...register("pinCode", {
                              required: "Please enter your pin code",
                              pattern: {
                                value: /^[1-9][0-9]{5}$/,
                                message: "Please enter your valid pin code",
                              },
                              maxLength: {
                                value: 6,
                                message: "Invalid pin code",
                              },
                              validate: validatePinCode,
                            })}
                          />
                          {errors.pinCode && (
                            <span className="invalid-feedback">
                              {errors.pinCode.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div
                        className={`form-group mb-3 ${styles.bookpackForm__lngFormInput}`}
                      >
                        <label htmlFor="language">Preferred Language* </label>
                        <DropDown
                          displayValue="display"
                          onSelect={handleLanguageSelection}
                          onRemove={handleLanguageSelection}
                          options={constant.BOOKPACK_LANGUAGES}
                          showCheckbox={true}
                          selectionLimit={2}
                          placeholder="Select a language (upto two)"
                          hidePlaceholder={true}
                          showArrow={true}
                          closeOnSelect={true}
                          customCloseIcon={
                            <StaticImage
                              src="../../images/bookPack/cross_2.png"
                              alt="cross_icon"
                              className="img-fluid"
                              placeholder="blurred"
                              quality={90}
                              style={{
                                width: "14px",
                                height: "14px",
                                marginLeft: "4px",
                              }}
                            />
                          }
                          customArrow={
                            <StaticImage
                              src="../../images/bookPack/arrow_dropdown.png"
                              alt="cross_icon"
                              className="img-fluid"
                              placeholder="blurred"
                              quality={90}
                              style={{
                                marginTop: "9px",
                              }}
                            />
                          }
                        />
                        {/* To show language error */}
                        {selectedLanguage.length === 0 && (
                          <span
                            className="text-danger"
                            style={{
                              fontSize: "0.875em",
                              display: showLanguageError,
                            }}
                          >
                            <p style={{ marginTop: "22px" }}>
                              Please select a language
                            </p>
                          </span>
                        )}
                      </div>
                    </div>

                    {props.showCouponSection && (
                      <div
                        className={`col-12 col-md-6 ${styles.bookpackForm_cpn_sec}`}
                      >
                        {hasApplied && props.coupon ? (
                          <div className={styles.bookpackForm_cpn_appliedSec}>
                            <span className="text-success">
                              {successMsg}
                              <span
                                className={`icon-cross ${styles.bookpackForm_cpn_can_icn}`}
                                aria-hidden="true"
                                role="presentation"
                                onClick={() => {
                                  setHasApplied(false);
                                  props.removedCoupon(true);
                                }}
                              />
                            </span>
                          </div>
                        ) : (
                          <div
                            className={`form-group ${styles.bookpackForm_cpn_label}`}
                          >
                            <label htmlFor="coupon">Coupon code (If any)</label>
                            <div
                              className={styles.bookpackForm_cpn_cta}
                              role="presentation"
                              onClick={handleCouponModal}
                              data-testid="bookpack_couponTxt"
                            >
                              <p>Coupon Code</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {/* End - Form_Input_Boxes */}

                    {/* Start - Terms_&_Conditions  */}
                    <div
                      className={`col-12 ${styles.bookPackFormV2__termsCol}`}
                    >
                      <div className={`form-group mb-3`}>
                        <div class="custom-control">
                          <div className="d-flex align-items-center">
                            <input
                              className={`custom-control-input ${styles.bookPackV2__checkbox}`}
                              type="checkbox"
                              defaultChecked={true}
                              id="flexCheckChecked"
                              {...register("terms", {
                                required: "Please check terms and conditions",
                              })}
                            />
                            <label
                              className={`custom-control-label ${styles.bookPackV2__checkLabel}`}
                              for="flexCheckChecked"
                              htmlFor="terms"
                            >
                              I agree to all{" "}
                              <Link to="/terms">
                                <span>Terms & Conditions</span>
                              </Link>
                            </label>
                          </div>
                          {errors.terms && (
                            <span className={styles.bookPackV2__invalid}>
                              {errors.terms.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* End - Terms_&_Conditions  */}

                    {/* Start - Buy_Now CTA Button */}
                    <div className={`col-12 mt-4`}>
                      <div className="buttonClass">
                        <span className="errorclass text-center text-danger d-flex justify-content-center">
                          {!isValid &&
                            isSubmitted &&
                            "Please fill all the fields correctly"}
                        </span>
                        <button
                          type="submit"
                          aria-label="Buy Now"
                          className={`${styles.bookpackForm_cta} ${
                            props.loading ? styles.bookpackForm_cta_event : ""
                          }`}
                          data-testid="bookPackForm_cta"
                          onClick={() => setShowLanguageError("block")}
                        >
                          <div className="bookBtn" id="book-now">
                            <span
                              className={`d-flex align-items-center ${
                                props.loading
                                  ? styles.bookpackForm_cta_txt_evnt
                                  : ""
                              }`}
                            >
                              <span
                                data-testid="bookPackForm_cta_txt"
                                className="bookBtn"
                              >
                                Buy Now at
                              </span>
                              <span
                                className={`bookBtn bookBtnAmt ${styles.bookpackForm_cta_amt}`}
                              >
                                <span>
                                  <p>{formatNumber(buttonPrice)}</p>
                                </span>
                              </span>
                            </span>

                            <span
                              className={`${styles.bookpackPilotForm_cta_loader} ${styles.bookpackForm_cta_loader}`}
                            >
                              <Loader
                                type="TailSpin"
                                color="#FFF"
                                height={30}
                                width={30}
                                radius={1}
                                visible={props.loading}
                              />
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                    {/* End - Buy_Now CTA Button */}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Coupon Modal */}
        <BookPackCouponModal
          show={showCouponModal}
          onHide={() => setShowCouponModal(false)}
          handleCouponUpdate={props.handleCouponUpdate}
          handleSuccessMsg={msg => {
            setHasApplied(true);
            setsuccessMsg(msg);
            setTimeout(() => setShowCouponModal(false), 2000);
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 8000);
          }}
          coupon={props.coupon}
          selectedPlan={props.selectedPlan}
          productId={props.productId}
          showCanvasModal={showCouponCanvasModal}
          onHideCanvasModal={() => setShowCouponCanvasModal(false)}
          windowSize={props.windowSize}
          couponType={props.couponType}
          toggleButtonValue={props.toggleButtonValue}
          planPrice={buttonPrice}
          couponMessageType={props.couponMessageType}
        />

        {/* Alert Message PopUp -> Non-Serviceable pincode */}
        <AlertPopUp
          text="Thank you for registering for launch invite! we will contact you soon."
          showAlertPopUp={showAlertPopUp}
          onHideAlertPopUpModal={() => setShowAlertPopUp(false)}
        />
      </div>
    </>
  );
});

export default BookPackPilotForm;
